import * as yup from 'yup';

import {
  AddCardRequest,
  CreditCardModel,
  CreditCardType,
  useAddCard,
  useCheckCard,
  useGuestBookingHyperPayInitializeAddCard,
  useGuestBookingPayFortInitializeAddCard,
  useInitializeAddCard,
  useInitHyperPayAddCard,
  useInitPayFortPayAddCard,
} from '@/logic/services';
import { Box, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { CHECKOUT, HYPER_PAY, PAYFORT } from '@/constants/payment';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';

import {
  GuestBookingCreditCardModel,
  GuestBookingPayInfoModel,
  useAuth,
  useDialog,
  useOrder,
  useSnack,
  useVerifiedOffer,
  useVerifiedOfferBin,
} from '@/logic/state';
import {
  ValidationCreditCardType,
  useCheckoutTokenization,
  useCreditCardValidation,
  useHyperPayTokenization,
  usePayFortTokenization,
} from '@/logic/hooks';

import { BankOfferHint } from './bank-offer-hint';
import { DateTime } from 'luxon';
import { ValidationTypes } from '@/constants/offers';
import { aN2eN, hashString, sliceStringWithoutSpaces } from '@/logic/shared';
import axios from 'axios';
import getConfig from 'next/config';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { CloseButton } from '@/components/shared/close-button';
import { DialogFormTitle } from '@/components/shared/dialog-form/dialog-form-title';
import { SupportedCreditCards } from '@/components/shared/add-credit-card/supported-credit-cards';
import { CreditCardNameInput } from '@/components/shared/add-credit-card/credit-card-name-input';
import { CreditCardNumberInput } from '@/components/shared/add-credit-card/credit-card-number-input';
import { CreditCardHolderInput } from '@/components/shared/add-credit-card/credit-card-holder-input';
import { CreditCardExpiryDateInput } from '@/components/shared/add-credit-card/credit-card-expiry-date-input';
import { CreditCardCvvInput } from '@/components/shared/add-credit-card/credit-card-cvv-input';
import { CreditCardSwitchInput } from '@/components/shared/add-credit-card/credit-card-switch-input';
import { DialogFormButton } from '@/components/shared/dialog-form/dialog-form-button';
import { onlyEnglishLettersAndSpaces } from '@/constants/regular-expressions';

export interface CreditCardElementProps {
  isLoading: boolean;
}

export interface CreditCardInputProps extends CreditCardElementProps {
  creditCardType: ValidationCreditCardType;
}

export interface CreditCardForm {
  isCardSavedForFuturePayments: boolean;
  cardName: string;
  cardHolder: string;
  expiryDate: string | null;
}

// TODO: check this card number validation 3777 7777 7777 7777 (it should return amex card type)

const { publicRuntimeConfig } = getConfig();
export const AddCreditCardForm: FC = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [isCardDefault, setIsCardDefault] = useState(true);

  const user = useAuth(s => s.user, shallow);
  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const setSnack = useSnack(s => s.setSnack, shallow);
  const [order, setOrder, guestBookingInfo, setGuestBookingInfo] = useOrder(
    s => [s.order, s.setOrder, s.guestBookingInfo, s.setGuestBookingInfo],
    shallow
  );
  const verifiedOffer = useVerifiedOffer(s => s.verifiedOffer, shallow);
  const verifiedOfferBin = useVerifiedOfferBin(s => s.verifiedOfferBin, shallow);

  const {
    creditCardNumber,
    creditCardCvv,
    isCreditCardNumberValid,
    isCreditCardCvvValid,
    creditCardType,
    handleCreditCardNumberChange,
    handleCreditCardCvvChange,
  } = useCreditCardValidation();
  const { isCheckoutLoading, checkoutTokenizationController, checkoutTokenization } =
    useCheckoutTokenization();
  const { isHyperPayLoading, hyperPayTokenizationController, hyperPayTokenization } =
    useHyperPayTokenization();
  const { isPayFortLoading, PayFortTokenizationController, PayFortTokenization } =
    usePayFortTokenization();

  const hashedCardBin = useMemo(() => {
    return {
      bh6: hashString(sliceStringWithoutSpaces(creditCardNumber, 6)),
      bh8: hashString(sliceStringWithoutSpaces(creditCardNumber)),
    };
  }, [creditCardNumber]);

  const initAddCard = useInitializeAddCard(
    {},
    {
      enabled: false,
    }
  );
  const initHyperPayAddCard = useInitHyperPayAddCard(
    {},
    {
      enabled: false,
    }
  );
  const initPayFortAddCard = useInitPayFortPayAddCard(
    {},
    {
      enabled: false,
    }
  );

  const addCard = useAddCard();
  const checkCard = useCheckCard();

  const guestBookingHyperPayInitAddCard = useGuestBookingHyperPayInitializeAddCard();
  const guestBookingPayFortInitAddCard = useGuestBookingPayFortInitializeAddCard();

  const schema = yup.object({
    isCardSavedForFuturePayments: yup.boolean(),
    cardName: yup.string().when('isCardSavedForFuturePayments', {
      is: true,
      then: schema =>
        schema
          .required()
          .max(
            40,
            intl.formatMessage({ id: 'my_credit_cards.card_name.validation.fail' })
          ),
      otherwise: schema => schema.optional(),
    }),
    cardHolder: yup
      .string()
      .required()
      .min(3, intl.formatMessage({ id: 'my_credit_cards.card_holder.validation.fail' }))
      .max(50, intl.formatMessage({ id: 'my_credit_cards.card_holder.validation.fail' }))
      .test(
        'onlyLettersSpaces',
        intl.formatMessage({ id: 'my_credit_cards.card_holder.validation.fail' }),
        value => onlyEnglishLettersAndSpaces.test(value)
      ),
    expiryDate: yup
      .date()
      .required()
      .min(
        DateTime.now().toFormat('yyyy-MM'),
        intl.formatMessage({ id: 'my_credit_cards.expiry_date.invalid' })
      )
      .max(
        DateTime.now().plus({ years: 20 }).toFormat('yyyy-MM'),
        intl.formatMessage({ id: 'my_credit_cards.expiry_date.invalid' })
      )
      .typeError(intl.formatMessage({ id: 'my_credit_cards.expiry_date.invalid' }))
      .nullable(),
  });

  const { isBookingFlow, paymentGateway, title } = dialog?.addCreditCardFormPayload;
  const {
    getValues,
    setValue,
    register,
    trigger,
    formState: { isValid, errors },
  } = useForm<CreditCardForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      isCardSavedForFuturePayments: !isBookingFlow,
      cardName: '',
      cardHolder: '',
      expiryDate: null,
    },
  });

  useEffect(() => {
    !order?.isApplePaySelected &&
      setOrder(prevState => ({
        ...prevState,
        isApplePaySelected: false,
      }));
  }, [order?.isApplePaySelected]);

  const isFormValid = isValid && isCreditCardNumberValid && isCreditCardCvvValid;

  const isLoading =
    isCheckoutLoading ||
    isHyperPayLoading ||
    isPayFortLoading ||
    addCard.isLoading ||
    checkCard.isLoading ||
    guestBookingHyperPayInitAddCard.isLoading ||
    guestBookingPayFortInitAddCard.isLoading ||
    initAddCard.isFetching ||
    initHyperPayAddCard.isFetching ||
    initPayFortAddCard.isFetching;

  const formTitle =
    isBookingFlow && order?.isGuestBookingEnabled && !user?.isCompleteProfile
      ? 'checkout.pay_with_card'
      : title
      ? title
      : 'my_credit_cards.new_credit_card';

  const handleCloseClick = () => {
    setDialog({});
  };

  const handleFormSwitchChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (isBookingFlow) {
      setValue('isCardSavedForFuturePayments', e.target.checked, {
        shouldValidate: true,
      });
    } else {
      setIsCardDefault(e.target.checked);
    }
  };

  const handleCheckoutTokenization = (abortParallelTokenization = false) => {
    const expiryDateArr = getValues('expiryDate').split('-');
    const expiryMonth = Number(expiryDateArr[1]);
    const expiryYear = Number(expiryDateArr[0]);

    return checkoutTokenization({
      checkoutUrl: publicRuntimeConfig.checkoutTokenUrl,
      checkoutKey: publicRuntimeConfig.checkoutPublicKey,
      creditCardNumber,
      creditCardCvv,
      creditCardHolder: getValues('cardHolder'),
      expiryMonth,
      expiryYear,
      errorCallback: () => {
        if (abortParallelTokenization) {
          queryClient.cancelQueries([
            {
              method: 'get',
              url: 'cards/hyper-pay/initialize-add-card',
            },
          ]);
          queryClient.cancelQueries([
            {
              method: 'get',
              url: 'cards/payfort/initialize-add-card',
            },
          ]);
          hyperPayTokenizationController.abort();
          PayFortTokenizationController.abort();
        }
        throw new Error('my_credit_cards.payment_gateway.fail');
      },
    });
  };

  const handlePayFortTokenization = async (
    abortParallelTokenization = false,
    initializedCardsData
  ) => {
    try {
      let payFortData;

      if (order?.isGuestBookingEnabled && isBookingFlow) {
        const data = await guestBookingPayFortInitAddCard.safeMutateAsync(
          {
            data: {
              firstName: guestBookingInfo?.firstName,
              lastName: guestBookingInfo?.lastName,
              phonenumber: guestBookingInfo?.phoneNumber,
              email: guestBookingInfo?.email,
            },
            headers: {
              'x-session-id': order?.userSessionId,
            },
          },
          {
            onError: () => {
              throw new Error();
            },
          }
        );

        payFortData = data.payfort;
      } else {
        if (initializedCardsData) {
          if (initializedCardsData.status === 'error') {
            throw new Error();
          }
          payFortData = initializedCardsData.data.payfort;
        } else {
          const data = await initPayFortAddCard.refetch();

          if (data.status === 'error') {
            throw new Error();
          }

          payFortData = data.data.payfort;
        }
      }
      const expiryDateArr = getValues('expiryDate').split('-');
      const expiryMonth = Number(expiryDateArr[1]);
      const expiryYear = Number(expiryDateArr[0]);
      return PayFortTokenization({
        PayFortUrl: publicRuntimeConfig.PayFortTokenUrl,
        creditCardNumber,
        creditCardHolder: getValues('cardHolder'),
        expiryMonth,
        expiryYear,
        ...payFortData,
        errorCallback: (errorMessage: string) => {
          setSnack({ isOpen: true, message: errorMessage, type: 'error' });
          throw new Error();
        },
      });
    } catch (error) {
      if (abortParallelTokenization) {
        queryClient.cancelQueries([
          {
            method: 'get',
            url: 'cards/hyper-pay/initialize-add-card',
          },
        ]);
        checkoutTokenizationController.abort();
        hyperPayTokenizationController.abort();
      }
      throw new Error('my_credit_cards.payment_gateway.fail');
    }
  };

  const handleHyperPayTokenization = async (
    abortParallelTokenization = false,
    initializedCardsData
  ) => {
    try {
      let hyperPayCheckId = '';

      if (order?.isGuestBookingEnabled && isBookingFlow) {
        const data = await guestBookingHyperPayInitAddCard.safeMutateAsync(
          {
            data: {
              firstName: guestBookingInfo?.firstName,
              lastName: guestBookingInfo?.lastName,
              phonenumber: guestBookingInfo?.phoneNumber,
              email: guestBookingInfo?.email,
              ...hashedCardBin,
            },
            headers: {
              'x-session-id': order?.userSessionId,
            },
          },
          {
            onError: () => {
              throw new Error();
            },
          }
        );

        hyperPayCheckId = data.checkId;
      } else {
        if (initializedCardsData) {
          if (initializedCardsData.status === 'error') {
            throw new Error();
          }
          hyperPayCheckId = initializedCardsData.data.checkId;
        } else {
          const data = await initHyperPayAddCard.refetch();

          if (data.status === 'error') {
            throw new Error();
          }

          hyperPayCheckId = data.data.checkId;
        }
      }

      const expiryDateArr = getValues('expiryDate').split('-');
      const expiryMonth = expiryDateArr[1];
      const expiryYear = expiryDateArr[0];

      return hyperPayTokenization({
        hyperPayUrl: publicRuntimeConfig.hyperPayTokenUrl,
        hyperPayCheckId,
        creditCardNumber,
        creditCardCvv,
        creditCardHolder: getValues('cardHolder'),
        creditCardType,
        expiryMonth,
        expiryYear,
        errorCallback: () => {
          throw new Error();
        },
      });
    } catch (error) {
      if (abortParallelTokenization) {
        queryClient.cancelQueries([
          {
            method: 'get',
            url: 'cards/payfort/initialize-add-card',
          },
        ]);
        checkoutTokenizationController.abort();
        PayFortTokenizationController.abort();
      }
      throw new Error('my_credit_cards.payment_gateway.fail');
    }
  };

  const selectCard = (
    isCardValidToSelect: boolean,
    newCard: CreditCardModel,
    manualPay?: boolean
  ) => {
    if (!getValues('isCardSavedForFuturePayments')) {
      setOrder(prevState => ({
        ...prevState,
        newCardId: isCardValidToSelect ? newCard.id : null,
        temporaryAddedCreditCards: isCardValidToSelect
          ? [...prevState?.temporaryAddedCreditCards, newCard]
          : [...prevState?.temporaryAddedCreditCards],
      }));
      order?.handleCreditCardsCvvListChange?.({
        id: newCard.id,
        cvv: creditCardCvv,
      });
      if (!manualPay && isCardValidToSelect) {
        order?.handlePayOrder?.(newCard.id, creditCardCvv, false); // here
      }
    } else if (isBookingFlow) {
      setOrder(prevState => ({
        ...prevState,
        newCardId: isCardValidToSelect ? newCard.id : null,
      }));
    }
    setDialog({});
    if (getValues('isCardSavedForFuturePayments')) {
      setSnack({
        isOpen: true,
        message: 'my_credit_cards.add.success',
        type: 'success',
        id: 'card-added-successfully',
      });
      queryClient.invalidateQueries([
        {
          method: 'get',
          url: 'cards',
          params: {
            limit: 50,
            page: 1,
          },
        },
      ]);
    }
  };

  const submitData = (
    checkoutToken?: string,
    hyperPayCheckId?: string,
    payfortToken?: string
  ) => {
    const cardDetails: AddCardRequest = !getValues('isCardSavedForFuturePayments')
      ? {
          save: false,
          orderId: order?.id,
          ...(checkoutToken
            ? {
                token: checkoutToken,
              }
            : hyperPayCheckId
            ? {
                checkId: hyperPayCheckId,
              }
            : payfortToken
            ? {
                payfortToken,
              }
            : {}),
        }
      : {
          save: true,
          name: getValues('cardName'),
          isDefault: isBookingFlow ? false : isCardDefault,
          token: checkoutToken,
          checkId: hyperPayCheckId,
          payfortToken,
        };

    addCard.safeMutateAsync(
      {
        data: { ...cardDetails, ...hashedCardBin },
      },
      {
        onSuccess: data => {
          if (
            order?.offer &&
            order?.offer?.validationType === ValidationTypes.BIN &&
            isBookingFlow
          ) {
            checkCard.safeMutateAsync(
              {
                query: {
                  id: order?.id,
                },
                params: {
                  cardId: data.card.id.toString(),
                  bin: verifiedOfferBin,
                },
              },
              {
                onSuccess: () => selectCard(true, data.card),
                onError: error => {
                  if (axios.isAxiosError(error)) {
                    const errorCode: number = error?.response?.data?.code;

                    const cardConfirmDialogPayload = {
                      img: order?.offer?.logoUrl,
                      info:
                        order?.offer?.validationType === ValidationTypes.BIN
                          ? order.offer.bankName
                          : order.offer.hint,
                      title: order?.offer?.title,
                    };

                    // The card not the same one you verified
                    if (errorCode === 103) {
                      setDialog({
                        wrapper: true,
                        tabletMinHeight: 'auto',
                        isCardNotSameVerifiedConfirmDialog: true,
                        cardNotSameVerifiedConfirmDialogPayload: {
                          ...cardConfirmDialogPayload,
                          handleOkayClick: () => selectCard(false, data.card),
                          handleContinueClick: () => {
                            order?.handleOfferRemove?.({
                              successCallback: () => {
                                selectCard(true, data.card, true);
                              },
                              errorCallback: () => {
                                selectCard(false, data.card);
                              },
                            });
                            setDialog({});
                          },
                        },
                      });
                    }
                    // The card is not supported
                    else if (errorCode === 104) {
                      setDialog({
                        wrapper: true,
                        tabletMinHeight: 'auto',
                        isCardNotSupportedConfirmDialog: true,
                        cardNotSupportedConfirmDialogPayload: {
                          ...cardConfirmDialogPayload,
                          handleOkayClick: () => selectCard(false, data.card),
                          handleContinueClick: () => {
                            order?.handleOfferRemove?.({
                              successCallback: () => {
                                selectCard(true, data.card, true);
                              },
                              errorCallback: () => {
                                selectCard(false, data.card);
                              },
                            });
                            setDialog({});
                          },
                        },
                      });
                    }
                    // The card not the same one you applied
                    else if (errorCode === 107) {
                      setDialog({
                        wrapper: true,
                        tabletMinHeight: 'auto',
                        isCardNotSameAppliedConfirmDialog: true,
                        cardNotSameAppliedConfirmDialogPayload: {
                          ...cardConfirmDialogPayload,
                          handleOkayClick: () => selectCard(false, data.card),
                          handleContinueClick: () => {
                            order?.handleOfferRemove?.({
                              successCallback: () => {
                                selectCard(true, data.card, true);
                              },
                              errorCallback: () => {
                                selectCard(false, data.card);
                              },
                            });
                            setDialog({});
                          },
                        },
                      });
                    } else {
                      selectCard(false, data.card);
                    }
                  }
                },
              }
            );
          } else {
            selectCard(true, data.card);
          }
        },
        onError: error => {
          queryClient.invalidateQueries([
            {
              method: 'get',
              url: 'cards',
              params: {
                limit: 50,
                page: 1,
              },
            },
          ]);
        },
      }
    );
  };

  const handleGuestBookingSubmitData = (
    token: string,
    cardBin?: string,
    cardLast4?: string,
    expiryMonth?: string,
    expiryYear?: string
  ) => {
    const guestBookingCreditCard: GuestBookingCreditCardModel = {
      scheme:
        creditCardType === 'visa'
          ? CreditCardType.Visa
          : creditCardType === 'mastercard'
          ? CreditCardType.MasterCard
          : creditCardType === 'american-express'
          ? CreditCardType.Amex
          : null,
      last4: creditCardNumber.slice(-4),
    };
    const payInfo: GuestBookingPayInfoModel = {
      payToken: token,
      payCardCvv: creditCardCvv,
      payCardBin: cardBin,
      payCardLast4: cardLast4,
      payExpiryMonth: expiryMonth,
      payExpiryYear: expiryYear,
    };
    setGuestBookingInfo(prevState => ({
      ...prevState,
      guestBookingCreditCard,
      payInfo,
    }));
    order?.handleGuestBookingPayOrder?.(true, false, payInfo);
    setDialog({});
  };

  const handleSave = async () => {
    try {
      if (!creditCardType) {
        setSnack({
          isOpen: true,
          message: 'my_credit_cards.unsupported_card',
          type: 'error',
        });
      } else if (!getValues('isCardSavedForFuturePayments')) {
        if (paymentGateway === HYPER_PAY) {
          const hyperPayCheckId = await handleHyperPayTokenization(false, undefined);

          if (order?.isGuestBookingEnabled) {
            const expiryDateArr = getValues('expiryDate').split('-');
            const expiryMonth = expiryDateArr[1];
            const expiryYear = expiryDateArr[0];
            handleGuestBookingSubmitData(
              hyperPayCheckId,
              undefined,
              undefined,
              expiryMonth,
              expiryYear
            );
          } else {
            submitData(undefined, hyperPayCheckId, undefined);
          }
        } else if (paymentGateway === PAYFORT) {
          const payFortToken = await handlePayFortTokenization(false, undefined);

          if (order?.isGuestBookingEnabled) {
            const binIndex = payFortToken?.card_number?.indexOf('*');
            const bin = payFortToken?.card_number?.substring(0, binIndex);
            const last4 = payFortToken?.card_number?.slice(-4);
            const expiryDateArr = getValues('expiryDate').split('-');
            const expiryMonth = expiryDateArr[1];
            const expiryYear = expiryDateArr[0];
            handleGuestBookingSubmitData(
              payFortToken?.token_name,
              bin,
              last4,
              expiryMonth,
              expiryYear
            );
          } else {
            submitData(undefined, undefined, payFortToken?.token_name);
          }
        } else if (paymentGateway === CHECKOUT) {
          const checkoutTokenizationData = await handleCheckoutTokenization(false);
          if (order?.isGuestBookingEnabled) {
            handleGuestBookingSubmitData(
              checkoutTokenizationData.token,
              checkoutTokenizationData.bin,
              checkoutTokenizationData.last4,
              checkoutTokenizationData.expiry_month,
              checkoutTokenizationData.expiry_year
            );
          } else {
            submitData(checkoutTokenizationData.token);
          }
        }
      } else {
        const initializedCardsData = await initAddCard.refetch();
        const hyperPayPromise = handleHyperPayTokenization(true, initializedCardsData);
        const checkoutPromise = handleCheckoutTokenization(true);
        const payFortPromise = handlePayFortTokenization(true, initializedCardsData);
        const [hyperPayCheckId, checkoutTokenizationData, payFortToken] =
          await Promise.all([hyperPayPromise, checkoutPromise, payFortPromise]);
        submitData(
          checkoutTokenizationData.token,
          hyperPayCheckId,
          payFortToken?.token_name
        );
      }
    } catch (error) {}
  };

  return (
    <>
      <DialogTitle>
        <CloseButton disabled={isLoading} handleClick={handleCloseClick} />
      </DialogTitle>
      <DialogContent
        sx={{
          textAlign: 'center',
        }}
      >
        <DialogFormTitle message={formTitle} />
        <Box
          sx={{
            marginBottom: `calc(24px + ${pxToRem(23)})`,
          }}
        >
          <SupportedCreditCards />
          {isBookingFlow && verifiedOffer?.isBankOfferApplied && <BankOfferHint />}
        </Box>

        <Stack spacing='24px'>
          {getValues('isCardSavedForFuturePayments') && (
            <CreditCardNameInput
              isLoading={isLoading}
              isAutoFocus={isBookingFlow}
              isError={errors.cardName?.type && errors.cardName?.type !== 'required'}
              helperText={
                errors.cardName?.type && errors.cardName?.type !== 'required'
                  ? errors.cardName?.message
                  : ''
              }
              registerFunc={() => register('cardName')}
            />
          )}
          <CreditCardNumberInput
            creditCardType={creditCardType}
            creditCardNumber={creditCardNumber}
            isCreditCardNumberValid={isCreditCardNumberValid}
            isLoading={isLoading}
            handleChange={handleCreditCardNumberChange}
          />
          <CreditCardHolderInput
            isLoading={isLoading}
            isError={errors.cardHolder?.type && errors.cardHolder?.type !== 'required'}
            helperText={
              errors.cardHolder?.type && errors.cardHolder?.type !== 'required'
                ? errors.cardHolder?.message
                : ''
            }
            registerFunc={() => register('cardHolder')}
          />
          <CreditCardExpiryDateInput
            isLoading={isLoading}
            isError={errors.expiryDate?.type && errors.expiryDate?.type !== 'required'}
            helperText={errors.expiryDate?.message}
            value={getValues()?.expiryDate || null}
            registerFunc={() => register('expiryDate')}
            handleChange={e => {
              e && setValue('expiryDate', aN2eN(e?.toFormat('yyyy-MM')));
              trigger();
            }}
          />
          <CreditCardCvvInput
            creditCardType={creditCardType}
            creditCardCvv={creditCardCvv}
            isCreditCardCvvValid={isCreditCardCvvValid}
            isLoading={isLoading}
            handleChange={handleCreditCardCvvChange}
          />
        </Stack>

        {!(isBookingFlow && order?.isGuestBookingEnabled) && (
          <CreditCardSwitchInput
            isLoading={isLoading}
            isChecked={
              isBookingFlow ? getValues('isCardSavedForFuturePayments') : isCardDefault
            }
            label={
              isBookingFlow
                ? 'checkout.set_card_future_payments'
                : 'my_credit_cards.set_card_default'
            }
            handleChange={handleFormSwitchChange}
          />
        )}
      </DialogContent>
      <DialogActions>
        <DialogFormButton
          isLoading={isLoading}
          isDisabled={!isFormValid}
          label={
            isBookingFlow
              ? order?.isGuestBookingEnabled
                ? 'global.pay'
                : 'my_credit_cards.use_this_card'
              : 'global.save'
          }
          handleSave={handleSave}
        />
      </DialogActions>
    </>
  );
};
